import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, StyleSheet, Text, TouchableOpacity, View } from './rn';
import { fontSize, radius, spacing } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { formatMoney } from '../data/currencies';
import { createVoiceRecorder, useNativeRecorder, type VoiceRecorder, type VoiceRecording } from '../voice/recorder';
import { voiceMeterReading, voiceOrderMeterReading } from '../voice/policy';

export function formatElapsed(seconds: number): string {
  const tam = Math.max(0, Math.floor(seconds));
  return `${Math.floor(tam / 60)}:${String(tam % 60).padStart(2, '0')}`;
}

type Props = {
  visible: boolean;
  pricePerMinute: number;
  currency: string;
  language: string;
  onCancel: () => void;
  onComplete: (recording: VoiceRecording) => void;
  /**
   * 9.1.11 — doluysa bu bir SİPARİŞ teslimidir: tavan bu süre, taksimetre yok.
   * Boşsa serbest gönderim, yani 60 dakika ve artan tutar.
   */
  orderSeconds?: number | null;
  /** Testte gerçek native cihaz yerine sahtesi geçilebilsin diye. */
  createRecorder?: () => VoiceRecorder;
};

/**
 * Kayıt yüzeyi ve taksimetre (9.1.6b · 9.1.6 · D-559).
 *
 * Serbest gönderimde tavan artık 60 dakika ve **kimse onu onaylamıyor**;
 * ödeyen, kayıt düğmesini basılı tutan kişinin kendisi. Korumanın tamamı bu
 * ekranda: geçen süre ve **o ana kadarki tutar** kayıt sürerken görünüyor.
 * D-559 tavanı yükseltmeyi buna bağlamıştı.
 *
 * Tutar `voiceMeterReading` üzerinden `voiceNoteCost` ile aynı formülden
 * geliyor; ayrı bir hesap, gösterilen ile kesilen arasında fark doğururdu.
 */
export default function VoiceRecorderSheet({
  visible, pricePerMinute, currency, language, onCancel, onComplete, createRecorder,
  orderSeconds,
}: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  // Native cihaz KANCADAN geliyor; paket başka bir yol vermiyor. Bu yüzden
  // `visible` false iken de çağrılıyor — kanca sırası sabit kalmalı.
  const native = useNativeRecorder();
  const [seconds, setSeconds] = useState(0);
  const cihaz = useRef<VoiceRecorder | null>(null);
  const bitti = useRef(false);

  /**
   * Çağrılar bir ref'te tutuluyor, doğrudan bağımlılık olarak DEĞİL. Bunları
   * veren ekran her çizimde yeni ok işlevi üretiyor; efekt onlara bağlı olsaydı
   * her çizimde yeni bir kayıt cihazı doğar, öncekisi de durdurulmadan bırakılırdı.
   */
  const guncel = useRef({ pricePerMinute, onCancel, onComplete, createRecorder, orderSeconds });
  guncel.current = { pricePerMinute, onCancel, onComplete, createRecorder, orderSeconds };

  const kapat = useCallback(async (gonder: boolean) => {
    const aygit = cihaz.current;
    cihaz.current = null;
    if (!aygit) { guncel.current.onCancel(); return; }
    if (!gonder) {
      await aygit.cancel();
      guncel.current.onCancel();
      return;
    }
    const kayit = await aygit.stop();
    // Boş kayıt gönderilmez: alıcıya bir şey söylemez ama ücret işletirdi.
    if (!kayit) { guncel.current.onCancel(); return; }
    guncel.current.onComplete(kayit);
  }, []);

  useEffect(() => {
    if (!visible) return;
    let iptal = false;
    bitti.current = false;
    setSeconds(0);
    const aygit = guncel.current.createRecorder?.() ?? createVoiceRecorder(native);
    aygit.onTick((gecen) => {
      if (iptal) return;
      setSeconds(gecen);
      // Kayıt tavanda kendi duruyor; ekran da o anda gönderime geçiyor ki
      // kullanıcı durmuş bir cihazın karşısında beklemesin.
      const siparis = guncel.current.orderSeconds;
      const okuma = siparis != null
        ? voiceOrderMeterReading(gecen, siparis)
        : voiceMeterReading(gecen, guncel.current.pricePerMinute);
      if (okuma.limitReached && !bitti.current) {
        bitti.current = true;
        void kapat(true);
      }
    });
    cihaz.current = aygit;
    void aygit.start().then((basladi) => {
      if (!basladi && !iptal) guncel.current.onCancel();
    });
    return () => { iptal = true; };
  }, [visible, kapat, native]);

  // İki okuma da hesaplanıyor, gösterilen biri: dallanma yalnız görünürde
  // kalsın diye. İkisi de saf hesap, maliyeti yok.
  const siparis = orderSeconds != null;
  const serbest = voiceMeterReading(seconds, pricePerMinute);
  const siparisOkuma = voiceOrderMeterReading(seconds, orderSeconds ?? 0);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={() => kapat(false)}>
      <View style={styles.overlay}>
        <View style={[styles.sheet, { backgroundColor: colors.white }]}>
          <Text style={[styles.title, { color: colors.gray400 }]}>
            {t(siparis ? 'message.voiceOrderRecording' : 'message.voiceRecording')}
          </Text>
          <Text testID="voice-elapsed" style={[styles.elapsed, { color: colors.black }]}>
            {formatElapsed(seconds > 0 ? seconds : 0)}
          </Text>
          {siparis ? (
            <>
              <Text testID="voice-order-remaining" style={[styles.amount, { color: colors.gray600 }]}>
                {t('message.voiceOrderRemaining', { length: formatElapsed(siparisOkuma.remainingSeconds) })}
              </Text>
              {/* Kısa teslimin sonucu ÖNCEDEN söyleniyor: satıcı erken
                  bitirirse emanetin tamamını almadığını burada öğrenmeli,
                  cüzdanına bakınca değil (D-573). */}
              <Text testID="voice-order-short-note" style={[styles.note, { color: colors.gray400 }]}>
                {t('message.voiceOrderShortNote')}
              </Text>
            </>
          ) : (
            <Text testID="voice-meter" style={[styles.amount, { color: colors.gray600 }]}>
              {formatMoney(serbest.amount, currency, language)}
            </Text>
          )}
          <View style={styles.actions}>
            <TouchableOpacity testID="voice-cancel" style={styles.action} onPress={() => kapat(false)}>
              <Text style={[styles.actionText, { color: colors.gray600 }]}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="voice-send"
              style={[styles.action, styles.send, { backgroundColor: colors.black }]}
              onPress={() => kapat(true)}>
              <Text style={[styles.actionText, { color: colors.white }]}>{t('message.priceChangedSend')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.35)' },
  sheet: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xl,
    borderTopLeftRadius: radius.layerOuter,
    borderTopRightRadius: radius.layerOuter,
    alignItems: 'center',
  },
  title: { fontSize: fontSize.sm },
  elapsed: { fontSize: 40, fontWeight: '700', marginTop: spacing.sm },
  amount: { fontSize: fontSize.md, marginTop: spacing.xs },
  note: { fontSize: fontSize.sm, marginTop: spacing.xs, textAlign: 'center' },
  actions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.lg, alignSelf: 'stretch' },
  action: { flex: 1, minHeight: 48, borderRadius: radius.control, alignItems: 'center', justifyContent: 'center' },
  send: {},
  actionText: { fontSize: fontSize.md, fontWeight: '600' },
});
